import { useState } from "react";
import { useManagerGatewayConfig } from "../../../hooks/gym/useManagerGatewayConfig";
import { useOAuthConnectionResult } from "../../../hooks/gym/useOAuthConnectionResult";
import { useCurrentGym } from "../../../hooks/gym/useCurrentGym";
import { useModuleEnabled } from "../../../hooks/gym/useModuleEnabled";
import {
  useGatewayProviders,
  type GatewayProvider,
} from "../../../hooks/gateway/useGatewayProviders";
import { ProviderCard } from "./providerCard";
import { ProviderConfigDialog } from "./providerConfigDialog";
import { LoadingSpinner } from "../../common/loadingSpinner";

export const GymGatewayConfig = () => {
  const { gatewayConfig, isLoading } = useManagerGatewayConfig();
  const { data: currentGym } = useCurrentGym();
  const isOnlinePaymentsEnabled = useModuleEnabled("onlinePayments");
  const { providers } = useGatewayProviders();
  useOAuthConnectionResult();

  const [selectedProvider, setSelectedProvider] =
    useState<GatewayProvider | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <LoadingSpinner
        message="Cargando configuracion de pasarela"
        size="sm"
        className="py-8"
      />
    );
  }

  const configuredProviders = gatewayConfig?.providers ?? [];
  const hasAnyConfigured = configuredProviders.some((p) => p.configured);

  const getProviderStatus = (providerKey: string) =>
    configuredProviders.find((p) => p.provider === providerKey);

  const handleCardClick = (provider: GatewayProvider) => {
    if (!provider.available) return;
    setSelectedProvider(provider);
    setConfigDialogOpen(true);
  };

  const handleDialogClose = () => {
    setConfigDialogOpen(false);
    setSelectedProvider(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-size-lg font-bold text-global-primary">
          Pasarelas de Pago
        </h2>
        <p className="text-size-xs text-global-muted mt-0.5">
          Configura los proveedores de pago online para tu gimnasio
        </p>
      </div>

      {hasAnyConfigured && isOnlinePaymentsEnabled && (
        <div className="flex items-center gap-2 rounded-lg border border-global-success/20 bg-global-success/5 px-3 py-2.5">
          <div className="w-2 h-2 rounded-full shrink-0 bg-global-success" />
          <p className="text-size-xs font-medium text-global-success">
            Pagos online activos
          </p>
        </div>
      )}

      {hasAnyConfigured && !isOnlinePaymentsEnabled && (
        <div className="flex items-center gap-2 rounded-lg border border-global-warning/20 bg-global-warning/5 px-3 py-2.5">
          <svg
            className="w-4 h-4 shrink-0 text-global-warning"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-size-xs font-medium text-global-warning">
            Credenciales configuradas. Contacta al administrador para activar
            los pagos online.
          </p>
        </div>
      )}

      {!hasAnyConfigured && (
        <div className="flex items-center gap-2 rounded-lg bg-global-overlay-secondary border border-global-secondary px-3 py-2.5">
          <div className="w-2 h-2 rounded-full shrink-0 bg-[var(--color-muted-text)]" />
          <p className="text-size-xs text-global-muted">
            Configura al menos un proveedor para activar pagos online
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {providers.map((provider) => {
          const status = getProviderStatus(provider.key);
          return (
            <ProviderCard
              key={provider.key}
              provider={provider}
              configured={status?.configured ?? false}
              configuredAt={status?.configuredAt ?? undefined}
              configuredBy={status?.configuredBy ?? undefined}
              connectionType={status?.connectionType}
              onConfigure={() => handleCardClick(provider)}
            />
          );
        })}
      </div>

      {selectedProvider && (
        <ProviderConfigDialog
          provider={selectedProvider}
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          configured={
            getProviderStatus(selectedProvider.key)?.configured ?? false
          }
          onSaved={handleDialogClose}
          onDeleted={handleDialogClose}
          gymPublicId={currentGym?.publicId}
          connectionType={
            getProviderStatus(selectedProvider.key)?.connectionType
          }
          mpUserId={getProviderStatus(selectedProvider.key)?.mpUserId}
          webhookUrl={getProviderStatus(selectedProvider.key)?.webhookUrl}
        />
      )}
    </div>
  );
};
